import { useState } from 'react';
import { Link } from 'react-router-dom';
import { trpc } from '../../lib/trpc';
import type { TastingNote } from '@wineryxplorer/shared';

const SCORE_LABELS: { min: number; max: number; label: string }[] = [
  { min: 50, max: 60, label: 'Not for me' },
  { min: 61, max: 70, label: 'Below average' },
  { min: 71, max: 80, label: 'Good' },
  { min: 81, max: 90, label: 'Very good' },
  { min: 91, max: 100, label: 'Outstanding' },
];

const getScoreLabel = (score: number) => {
  const match = SCORE_LABELS.find((l) => score >= l.min && score <= l.max);
  return match?.label ?? '';
};

const getScoreColor = (score: number) => {
  if (score >= 91) return 'text-green-700 bg-green-50';
  if (score >= 81) return 'text-primary-700 bg-primary-50';
  if (score >= 71) return 'text-yellow-700 bg-yellow-50';
  if (score >= 61) return 'text-orange-700 bg-orange-50';
  return 'text-red-700 bg-red-50';
};

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const PAGE_SIZE = 20;

export const TastingNotesTab = () => {
  const [offset, setOffset] = useState(0);

  const { data, isLoading, error } = trpc.tastingNote.list.useQuery({
    limit: PAGE_SIZE,
    offset,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-700" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load tasting notes. Please try again.</p>
      </div>
    );
  }

  const notes = (data?.items ?? []) as TastingNote[];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  if (notes.length === 0 && offset === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Tasting Notes Yet</h3>
        <p className="text-sm text-gray-500">
          Visit a wine page and add a tasting note to track your impressions.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        {total} tasting note{total !== 1 ? 's' : ''}
      </p>

      {notes.map((note) => {
        const wine = note.wine as unknown as { _id: string; name: string } | null;
        const wineId = wine?._id ?? (note.wine as unknown as string);
        const wineName = wine?.name ?? 'Unknown Wine';

        return (
          <div key={note._id as string} className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <Link
                  to={`/wines/${wineId}`}
                  className="text-base font-medium text-primary-600 hover:text-primary-700 hover:underline"
                >
                  {wineName}
                </Link>
                <div className="flex items-center gap-3 mt-1.5">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getScoreColor(note.score)}`}
                  >
                    {note.score} — {getScoreLabel(note.score)}
                  </span>
                  <span className="text-sm text-gray-500">{formatDate(note.dateTasted)}</span>
                </div>
                <p className="mt-2 text-sm text-gray-700 line-clamp-2">{note.notes}</p>
              </div>
            </div>
          </div>
        );
      })}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <button
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            disabled={offset === 0}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-500">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setOffset(offset + PAGE_SIZE)}
            disabled={offset + PAGE_SIZE >= total}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};
